/**
 * Wire protocol between the three parties of a phone-controlled table:
 *
 *   phone ⇄ server ⇄ host
 *
 * The server (server/relay.mjs) is a *dumb relay* with rooms: it understands only
 * the envelope layer (`Up*` / `Down*`) and never looks inside `d` payloads. The
 * app layer (`Phone*` / `Host*`) is host ⇄ phone private vocabulary carried
 * opaquely through the relay. Like `api.ts`, the envelope shapes are mirrored by
 * hand in server/index.mjs — the two sides must agree exactly.
 *
 * The phone vocabulary is deliberately tiny: a phone can say hello, rename
 * itself, and **tap something**. There is no button stream, no cursor, no
 * virtual d-pad — a touchscreen is a pointing device, so the host sends the
 * things you can point at and the phone sends back which one you touched.
 *
 * This module has zero imports so the phone bundle, the host bundle, and tests
 * can all pull it in without dragging the engine along.
 */

/* ----------------------------------------------------------- envelope: up */

/** First frame a host sends. `code` asks to reclaim a room after a reconnect. */
export type UpHello =
  | { t: 'host'; code?: string }
  | { t: 'join'; code: string };

export type UpFromHost =
  | { t: 'to'; peer: string; d: HostMsg }
  | { t: 'all'; d: HostMsg }
  /** Kick a phone (seat released in the lobby). */
  | { t: 'drop'; peer: string };

export type UpFromPhone = { t: 'msg'; d: PhoneMsg };

/* --------------------------------------------------------- envelope: down */

export type DownToHost =
  | { t: 'room'; code: string }
  | { t: 'peer'; peer: string; on: boolean }
  | { t: 'from'; peer: string; d: PhoneMsg }
  | { t: 'deny'; reason: string };

export type DownToPhone =
  | { t: 'joined'; code: string }
  | { t: 'msg'; d: HostMsg }
  /** Host presence changed (TV refreshing, room closed…). */
  | { t: 'stat'; host: boolean }
  | { t: 'deny'; reason: string };

/* -------------------------------------------------------- app: phone→host */

export type PhoneMsg =
  /** Sent once per connection; `token` is the phone's persistent identity. */
  | { k: 'hello'; token: string; name: string }
  /**
   * The only play verb. `id` names something the host offered in the current
   * view — an option row, a plot tile (`tile:4`), a header control. `n` echoes
   * the view's nonce so a tap aimed at a panel that has already changed under
   * the player's thumb is discarded rather than misfiring.
   */
  | { k: 'tap'; id: string; n: number }
  | { k: 'name'; name: string };

/* -------------------------------------------------------- app: host→phone */

export type HostMsg =
  | { k: 'view'; v: PhoneView }
  /** Ask the phone to vibrate (inspection drama). */
  | { k: 'buzz'; ms: number };

/** One tappable row in a phone's panel. */
export interface PhoneOption {
  id: string;
  label: string;
  /** Right-aligned detail, e.g. a cost. */
  detail?: string;
  disabled?: boolean;
  /** Shown under the list when a disabled row is tapped. */
  reason?: string;
  tone?: 'good' | 'bad' | 'accent';
}

/**
 * One patch of the owner's 3×3 plot, as their phone draws it. This is the
 * heart of the touch design: your field is in your hand, so you point at the
 * patch you mean instead of walking a cursor to it.
 *
 * `secret` rides only in the owning seat's view — the shared screen and every
 * other phone see `state: 'tarp'` with no crop name.
 */
export interface PhoneTile {
  /** Growth stage as the owner sees it. */
  state: 'bare' | 'growing' | 'ripe' | 'tarp';
  /** Crop name for an open planting, e.g. "Barley". */
  crop?: string;
  /** What is under the tarp — private to the owner. */
  secret?: string;
  /** Growth fraction 0..1, for the little progress bar. */
  grown?: number;
  /** Ready to harvest (drawn with a glow). */
  ripe?: boolean;
  /** One-word verb a tap performs now; absent = not tappable this instant. */
  verb?: 'Sow' | 'Water' | 'Harvest';
  /** Why it can't be tapped, when it can't. */
  note?: string;
}

/** A header control on the phone. */
export interface PhoneSys {
  /** Pause/resume the table. */
  pause?: 'pause' | 'resume';
  /** Show the day's ledger. */
  ledger?: boolean;
  /** Close whatever panel is open (back one level). */
  back?: boolean;
}

/**
 * Everything a phone renders. The host owns all game truth and pushes a fresh
 * view whenever a seat's slice changes; phones are stateless projectors of it.
 * Private information (cart contents, tarp secrets, plot detail) rides ONLY in
 * the owning seat's view — this structure is the game's secrecy boundary.
 */
export interface PhoneView {
  /** Monotonic per-seat nonce; taps echo it so a stale tap can be ignored. */
  n: number;
  screen: 'lobby' | 'game' | 'results';
  seat: number;
  /** Seat display name, e.g. "RED". */
  seatName: string;
  /** Seat colour, css hex. */
  color: string;
  name: string;
  coins: number;
  season?: number;
  seasons?: number;
  /** You wear the Inspector badge this season. */
  badge?: boolean;
  /** Headline over the contextual area. */
  title?: string;
  /** Secondary prose lines (waiting notes, what a rival just did…). */
  lines?: string[];
  /** Your 3×3 plot, row-major. Tappable when a tile carries a `verb`. */
  plot?: PhoneTile[];
  /** Actions that aren't tied to one patch — harvest all, snoop, odd jobs. */
  actions?: PhoneOption[];
  /** A focused decision: crop choice, cart loading, gate verdict… */
  options?: PhoneOption[];
  /** Header controls. */
  sys?: PhoneSys;
  /** Your cart, oldest first — private to you. */
  cart?: string[];
  /** Ledger lines, when the ledger panel is open. */
  log?: string[];
  /** True while it is specifically your decision the table waits on. */
  hot?: boolean;
  /** Actions left in your turn, for the little pip row. */
  ap?: number;
}

/* ----------------------------------------------------------------- helpers */

/** Room codes: 4 letters, no ambiguous glyphs (I/O/0/1). */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
export const ROOM_CODE_LENGTH = 4;

export function isRoomCode(s: string): boolean {
  if (s.length !== ROOM_CODE_LENGTH) return false;
  for (const ch of s) if (!ROOM_CODE_ALPHABET.includes(ch)) return false;
  return true;
}

/** The WebSocket endpoint, same-origin. Vite dev proxies it to the server. */
export function wsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/ws`;
}

/** Path phones visit; the server serves the controller page here. */
export const CONTROLLER_PATH = '/play';
